import { useTheme } from "next-themes";
import { Toaster as Sonner, toast as sonnerToast } from "sonner";
import { CheckCircle2, AlertTriangle, AlertCircle, Info } from "lucide-react";
import { useIsMobile } from "@/hooks/useMobile";

type ToasterProps = React.ComponentProps<typeof Sonner>;

const iconClass =
  "size-5 shrink-0 motion-safe:animate-in motion-safe:zoom-in-75 motion-safe:fade-in motion-safe:duration-fast";

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();
  const isMobile = useIsMobile();

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group notranslate"
      position={isMobile ? "top-center" : "bottom-right"}
      // Appli installée sur iOS (barre d'état translucide) : les toasts du haut
      // passaient sous l'heure et la Dynamic Island.
      mobileOffset={{ top: "calc(env(safe-area-inset-top, 0px) + 16px)" }}
      icons={{
        success: <CheckCircle2 className={iconClass + " text-success"} />,
        error: <AlertCircle className={iconClass + " text-destructive"} />,
        warning: <AlertTriangle className={iconClass + " text-warning"} />,
        info: <Info className={iconClass + " text-primary"} />,
      }}
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-xl group-[.toaster]:rounded-xl group-[.toaster]:border-l-4 group-[.toaster]:border-l-primary text-payfit-body",
          description: "group-[.toast]:text-payfit-muted mt-0.5",
          actionButton: "group-[.toast]:bg-primary group-[.toast]:text-payfit-on-dark text-payfit-body rounded-lg",
          cancelButton: "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground text-payfit-body rounded-lg",
          success: "group-[.toaster]:border-l-success",
          error: "group-[.toaster]:border-l-destructive",
          info: "group-[.toaster]:border-l-primary",
          warning: "group-[.toaster]:border-l-warning",
        },
      }}
      {...props}
    />
  );
};

// Re-export with the same shape as sonner's `toast` so existing imports
// `import { toast } from "@/components/ui/sonner"` keep working unchanged.
// Existing imports from "sonner" continue to work with default durations.
const toast = sonnerToast;

export { Toaster, toast };
